import React, { ChangeEvent, useState } from "react";
import styles from "./AddKnowledgeModal.module.css";
import clsx from "clsx";
import { addSopKnowledge } from "../../../../../../api/SopApiCall";
import { showConsoleError } from "../../../../../../util/ConsoleMessage";
import { RootState } from "../../../../../../store/common/store";
import { useSelector } from "react-redux";
import { KnowledgeType } from "../../../../../../util/KnowledgeType";
import { IntentDataModel } from "../../../../../../api/responsePayload/IntentResponse";
import { AddIntentKnowledge } from "../../../../../../api/IntentApiCall";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../store/common/hooks";
import { fetchSopKnowledge } from "../../../../../../store/sop.slice";
import { fetchIntents } from "../../../../../../store/intent.slice";

interface AddKnowledgeModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const AddKnowledgeModal: React.FC<AddKnowledgeModalProps> = ({
  isOpen = false,
  onClose,
}) => {
  const categoryOptions = [
    { name: "Internal Jargon", value: 1 },
    { name: "Business Term", value: 2 },
    { name: "Rewrite Intent", value: 3 },
    { name: "Error Code", value: 4 },
  ];

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");

  const dispatch = useAppDispatch();
  const { currentPage } = useSelector((state: RootState) => state.pagination);
  const knowledgeType = useAppSelector((s) => s.brain.knowledgeType);
  const [intent, setIntent] = useState<IntentDataModel>({
    category: 0,
    date_created: "",
    detailed_input: "",
    id: 0,
    last_updated: "",
    meaning: "",
    raw_input: "",
  });

  if (!isOpen) return null;

  const handleOnClose = () => {
    if (knowledgeType === KnowledgeType.SOP) {
      dispatch(fetchSopKnowledge({ page: currentPage }));
    } else if (knowledgeType === KnowledgeType.INTENT) {
      dispatch(fetchIntents({ page: currentPage }));
    }
    setTitle("");
    setContent("");
    setDescription("");
    onClose?.();
    setIntent({
      category: 0,
      date_created: "",
      detailed_input: "",
      id: 0,
      last_updated: "",
      meaning: "",
      raw_input: "",
    });

    setError("");
  };

  const handleAddKnowledge = async () => {
    if (knowledgeType === KnowledgeType.SOP) {
      try {
        if (
          title.trim() === "" ||
          content.trim() === "" ||
          description.trim() === ""
        ) {
          return;
        }
        await addSopKnowledge(title, description, content);
        handleOnClose();
      } catch (e: any) {
        showConsoleError(e);
        setError(e.data.error.data.title[0]);
      }
    } else {
      if (
        intent.meaning.trim() === "" ||
        intent.detailed_input.trim() === "" ||
        intent.raw_input.length === 0
      ) {
        return;
      }

      try {
        await AddIntentKnowledge(
          intent.detailed_input,
          intent.raw_input,
          intent.category,
          intent.meaning
        );
        handleOnClose();
      } catch (e: any) {
        showConsoleError(e);
        setError(e.data.error.data.raw_input[0]);
      }
    }
  };

  const handleOnChangeMeaning = (value: string) => {
    setIntent((prevIntent) => ({
      ...prevIntent,
      meaning: value,
    }));
  };

  const handleOnChangeDetailedInput = (value: string) => {
    setIntent((prevIntent) => ({
      ...prevIntent,
      detailed_input: value,
    }));
  };
  const handleOnCategoryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setIntent((prevIntent) => ({
      ...prevIntent,
      category: parseInt(event.target.value),
    }));
  };
  const handleOnRawInputChange = (values: string) => {
    setIntent((prevIntent) => ({
      ...prevIntent,
      raw_input: values,
    }));
  };

  const sopModalBody = (
    <div className={styles.form}>
      <div className={styles.field}>
        <label htmlFor="image-name">Title:</label>
        <input
          id="image-name"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor="image-description">Description:</label>
        <textarea
          id="image-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="image-description">Content:</label>
        <textarea
          id="image-description"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>
    </div>
  );

  const intentModalBody = (
    <div className={styles.form}>
      <div className={styles.field}>
        <div className={styles.field}>
          <p>
            <strong>Raw Input: </strong>
          </p>
          <input
            type="text"
            value={intent.raw_input}
            onChange={(e) => handleOnRawInputChange(e.target.value)}
            className={styles.inputField}
          />
        </div>
        <label htmlFor="image-name">Meaning:</label>
        <input
          id="image-name"
          type="text"
          value={intent.meaning}
          onChange={(e) => handleOnChangeMeaning(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor="image-description">Detailed Input:</label>
        <textarea
          id="image-description"
          value={intent.detailed_input}
          onChange={(e) => handleOnChangeDetailedInput(e.target.value)}
        />
      </div>
      <p>
        <strong>Category: </strong>
      </p>
      <div className={styles.field}>
        <select
          className={styles["category-select"]}
          value={intent.category}
          onChange={handleOnCategoryChange}
        >
          <option value="0">Select a category</option>
          {categoryOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div
            style={{
              flex: 1,
              flexDirection: "row",
              display: "flex",
              gap: "16px",
            }}
          ></div>
          <button className={styles.closeButton} onClick={handleOnClose}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {knowledgeType === KnowledgeType.SOP ? sopModalBody : intentModalBody}
          {error && <div className={styles.error}>{error}</div>}
        </div>
        <div className={styles.modalFooter}>
          <button
            className={clsx(styles.button, styles.warning)}
            onClick={handleOnClose}
          >
            Cancel
          </button>
          <button
            className={clsx(styles.button, styles.primary)}
            onClick={handleAddKnowledge}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddKnowledgeModal;
